import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';

declare var window: any;
declare var document: any;

@Component({
  selector: 'app-combo-chart',
  standalone: true,
  imports: [
    CommonModule,
    NgxChartsModule
  ],
  templateUrl: './combo-chart.component.html',
  styleUrl: './combo-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComboChartComponent implements AfterViewInit {
  @Input() barChartData: any[] = [];
  @Input() lineChartData: any[] = [];
  @Input() competitorAnalysisData: any = null; // New input for API data structure
  @Input() xAxisLabel: string = 'University';
  @Input() yAxisLabel: string = 'Score';
  @Input() legendTitle: string = 'AI Platforms';
  @Input() showGridLines: boolean = true;
  @Input() animations: boolean = true;
  @Input() noBorder: boolean = false;
  @Input() view: [number, number] = [700, 400];

  // Color schemes for combo chart - using string values for ngx-charts
  comboBarScheme = 'cool';
  lineChartScheme = 'vivid';

  // Combined data for the combo chart
  combinedData: any[] = [];

  // Cached processed data to avoid recalculation
  private _cachedBarData: any[] = [];
  private _cachedLineData: any[] = [];
  private _cachedFirstUniversityName: string = '';

  // Getter properties for template optimization
  get processedBarData(): any[] {
    return this.getProcessedBarData();
  }

  get processedLineData(): any[] {
    return this.getProcessedLineData();
  }

  get firstUniversityName(): string {
    return this.getFirstUniversityName();
  }

  constructor(private cdr: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    // Set view size safely for SSR - use fixed reasonable size
    if (window.innerWidth < 400) {
      this.view = [250, 300];
    } else if(window.innerWidth > 1500) {
      this.view = [900, 400];
    } else {
      this.view = [600, 400];
    }

    // Process data immediately without setTimeout
    this.prepareCombinedData();
    this.setLegendTitle();
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['barChartData'] || changes['lineChartData'] || changes['competitorAnalysisData']) {
      // Clear cache when data changes
      this.clearCache();
      // Process data immediately and mark for change detection
      this.prepareCombinedData();
      this.cdr.markForCheck();
    }
    if (changes['legendTitle']) {
      this.setLegendTitle();
      this.cdr.markForCheck();
    }
  }

  private clearCache(): void {
    this._cachedBarData = [];
    this._cachedLineData = [];
    this._cachedFirstUniversityName = '';
  }

  prepareCombinedData() {
    // Create combined data structure for combo chart
    // Bar data will be the first series, line data will be additional series
    this.combinedData = [];

    // Add bar chart data as the first series
    if (this.barChartData && this.barChartData.length > 0) {
      this.combinedData.push({
        name: 'Overall Scores',
        series: this.barChartData,
        type: 'bar'
      });
    }

    // Add line chart data as additional series
    if (this.lineChartData && this.lineChartData.length > 0) {
      this.lineChartData.forEach(lineData => {
        this.combinedData.push({
          ...lineData,
          type: 'line'
        });
      });
    }
  }

  // Method to get bar chart data with single color for all universities
  getBarChartDataWithSingleColor() {
    if (!this.barChartData || this.barChartData.length === 0) {
      return [];
    }

    // For bar chart, we need to return the data as-is but ensure single color
    // The color scheme will handle the single color
    return this.barChartData;
  }

  setLegendTitle() {
    if (typeof document !== 'undefined') {
      const legendTitleEl = document.querySelector('ngx-charts-legend .legend-title-text');
      if (legendTitleEl) {
        legendTitleEl.innerHTML = this.legendTitle || 'AI Platforms';
      }
    }
  }

  getLineColor(platformName: string): string {
    const colors = ['#DB4437', '#F4B400', '#0F9D58', '#AB47BC'];
    const lineData = this.getProcessedLineData();
    if (!lineData || !Array.isArray(lineData)) {
      return colors[0]; // Return first color as fallback
    }
    const index = lineData.findIndex(data => data.name === platformName);
    return colors[index % colors.length];
  }

  // Process API data structure for competitor analysis
  processCompetitorAnalysisData() {
    // // console.log('Processing competitor analysis data:', this.competitorAnalysisData);

    if (!this.competitorAnalysisData) {
      // // console.log('No competitor analysis data available');
      return { barData: [], lineData: [] };
    }

    const barData: any[] = [];
    const lineData: any[] = [];
    const xAxisLabels = this.competitorAnalysisData.x_axis || [];
    // // console.log('X-axis labels:', xAxisLabels);

    // Process series data from API
    this.competitorAnalysisData.series.forEach((series: any, index: number) => {
      // // console.log(`Processing series ${index}:`, series);

      // Force first series to be bars, others to be lines
      if (index === 0 || series.type === 'bar') {
        // // console.log('Processing bar series:', series.name);
        // Bar data (Your Brand) - map data array to x-axis labels
        series.data.forEach((value: number, dataIndex: number) => {
          if (xAxisLabels[dataIndex]) {
            barData.push({
              name: xAxisLabels[dataIndex],
              value: value
            });
          }
        });
      } else {
        // // console.log('Processing line series:', series.name);
        // Line data (Competitors) - map data array to x-axis labels
        const lineSeries = {
          name: series.name,
          series: series.data.map((value: number, dataIndex: number) => ({
            name: xAxisLabels[dataIndex] || `Property ${dataIndex + 1}`,
            value: value
          }))
        };
        lineData.push(lineSeries);
      }
    });

    // // console.log('Processed bar data:', barData);
    // // console.log('Processed line data:', lineData);

    return { barData, lineData };
  }


  // Get processed data for charts with caching
  getProcessedBarData() {
    if (this.competitorAnalysisData) {
      if (this._cachedBarData.length === 0) {
        const processed = this.processCompetitorAnalysisData();
        this._cachedBarData = processed.barData;
      }
      return this._cachedBarData;
    }
    return this.getBarChartDataWithSingleColor();
  }

  getProcessedLineData() {
    if (this.competitorAnalysisData) {
      if (this._cachedLineData.length === 0) {
        const processed = this.processCompetitorAnalysisData();
        this._cachedLineData = processed.lineData;
      }
      return this._cachedLineData;
    }
    return this.lineChartData;
  }

  // Get the name of the first university (bar data) with caching
  getFirstUniversityName(): string {
    if (this._cachedFirstUniversityName === '' && this.competitorAnalysisData && this.competitorAnalysisData.series && this.competitorAnalysisData.series.length > 0) {
      this._cachedFirstUniversityName = this.competitorAnalysisData.series[0].name;
    }
    return this._cachedFirstUniversityName;
  }
}
